import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import React, { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { SimpleRegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

interface Props {
	debtHoldings: SimpleRegularViewTableItemData[];
	defaultValue?: string | null;
	formFields: {
		pledgeFromString: string;
		pledgeFromHolderId: string;
		pledgeFromSecurityId: string;
	};
}

export const PledgeAgainstSelectComponent: React.FC<Props> = ({
	debtHoldings,
	defaultValue,
	formFields,
}) => {
	const tCapManage = useTranslationCapManage();
	const formContext = useFormContext();
	const { setValue, getValues } = formContext;

	const [selectOptions, setSelectOptions] = useState<{ value: string; label: string }[]>([]);
	const [selectedOption, setSelectedOption] = useState<string | null>(null);

	useEffect(() => {
		const currentString = getValues(formFields.pledgeFromString);

		const displayString = currentString || defaultValue;

		const debtOptions =
			debtHoldings?.map((holding) => ({
				value: `${holding.holderId}:${holding.securityId}`,
				label: `${holding.holderName}'s holding in ${holding.securityName}`,
			})) || [];

		const options = [...debtOptions];
		let valueToSelect = '';

		if (displayString) {
			const matchingOption = debtOptions.find((option) => option.label === displayString);

			if (matchingOption) {
				valueToSelect = matchingOption.value;
			} else {
				const customOption = {
					value: displayString,
					label: displayString,
				};
				options.push(customOption);
				valueToSelect = customOption.value;
			}

			if (!currentString) {
				setValue(formFields.pledgeFromString, displayString);
			}
		}

		setSelectOptions(options);
		setSelectedOption(valueToSelect);
	}, [debtHoldings, defaultValue, getValues, setValue, formFields]);

	return (
		<Controller
			name={formFields.pledgeFromString}
			control={formContext.control}
			render={() => (
				<CreatableSelect
					value={selectedOption}
					data={selectOptions}
					label={tCapManage('holdings.event.pledge_against')}
					placeholder={tCapManage('holdings.event.pledge_against_holding')}
					size="md"
					clearable
					getCreateLabel={(query) => `${tCapManage('holdings.event.save')}: "${query}"`}
					onCreate={(query) => {
						const matchingOption = selectOptions.find(
							(option) => option.label === query
						);
						if (matchingOption) {
							setSelectedOption(matchingOption.value);

							if (
								matchingOption.value.includes(':') &&
								matchingOption.value.split(':').length === 2
							) {
								const [holderId, securityId] = matchingOption.value.split(':');
								setValue(formFields.pledgeFromString, matchingOption.label, {
									shouldDirty: true,
								});
								setValue(formFields.pledgeFromHolderId, holderId, {
									shouldDirty: true,
								});
								setValue(formFields.pledgeFromSecurityId, securityId, {
									shouldDirty: true,
								});
							} else {
								setValue(formFields.pledgeFromString, matchingOption.label, {
									shouldDirty: true,
								});
								setValue(formFields.pledgeFromHolderId, null, {
									shouldDirty: true,
								});
								setValue(formFields.pledgeFromSecurityId, null, {
									shouldDirty: true,
								});
							}

							return matchingOption;
						}

						const newOption = { value: query, label: query };
						setSelectOptions((prev) => [...prev, newOption]);
						setSelectedOption(query);

						setValue(formFields.pledgeFromString, query, {
							shouldDirty: true,
						});
						setValue(formFields.pledgeFromHolderId, null, {
							shouldDirty: true,
						});
						setValue(formFields.pledgeFromSecurityId, null, {
							shouldDirty: true,
						});

						return newOption;
					}}
					onChange={(value) => {
						setSelectedOption(value);

						if (value === null) {
							setValue(formFields.pledgeFromString, '', { shouldDirty: true });
							setValue(formFields.pledgeFromHolderId, null, { shouldDirty: true });
							setValue(formFields.pledgeFromSecurityId, null, { shouldDirty: true });
							return;
						}

						const option = selectOptions.find((opt) => opt.value === value);

						if (!option) {
							return;
						}

						setValue(formFields.pledgeFromString, option.label, {
							shouldDirty: true,
						});

						if (value.includes(':') && value.split(':').length === 2) {
							const [holderId, securityId] = value.split(':');
							setValue(formFields.pledgeFromHolderId, holderId, {
								shouldDirty: true,
							});
							setValue(formFields.pledgeFromSecurityId, securityId, {
								shouldDirty: true,
							});
						} else {
							setValue(formFields.pledgeFromHolderId, null, {
								shouldDirty: true,
							});
							setValue(formFields.pledgeFromSecurityId, null, {
								shouldDirty: true,
							});
						}
					}}
				/>
			)}
		/>
	);
};
